/**
 * O nome da pessoa, do jeito que se fala com ela.
 *
 * O Google devolve o nome completo (`name`), e é ele que gravamos. Mas
 * "Bom dia, Calebe Matias de Oliveira" não é como ninguém cumprimenta ninguém -
 * saudação é primeiro nome, e o resto do nome é para documento.
 *
 * Cai para a string vazia quando não há nome: quem chama decide se mostra a
 * saudação sem nome ou não mostra saudação nenhuma.
 */
export function primeiroNome(nome: string | null | undefined): string {
  return (nome ?? '').trim().split(/\s+/)[0] ?? '';
}

/**
 * As iniciais, para quando não há foto.
 *
 * Duas letras quando o nome tem duas partes - a do primeiro nome e a do
 * **último**, que é como monograma se faz e como todo avatar de SaaS resolve.
 * Uma letra só quando é um nome simples, e o e-mail como último recurso para
 * quem entrou pelo Google sem nome no perfil.
 *
 * Partículas ("de", "da", "dos") não contam como sobrenome: "Calebe de Oliveira"
 * dá CO, e não CD.
 */
const PARTICULAS = new Set(['de', 'do', 'da', 'dos', 'das', 'e']);

export function iniciaisDoNome(nome: string | null | undefined, email?: string | null): string {
  const partes = (nome ?? '')
    .trim()
    .split(/\s+/)
    .filter((parte) => parte.length > 0 && !PARTICULAS.has(parte.toLowerCase()));

  if (partes.length === 0) return (email ?? '?').trim().charAt(0).toUpperCase() || '?';
  if (partes.length === 1) return partes[0]!.charAt(0).toUpperCase();

  return (partes[0]!.charAt(0) + partes[partes.length - 1]!.charAt(0)).toUpperCase();
}

/**
 * Bom dia, boa tarde ou boa noite, pela hora de quem lê.
 *
 * A hora é a **do navegador**, e não a de Brasília: um aluno em Portugal
 * abrindo às nove da noite de lá não quer ler "bom dia" porque no Brasil é
 * tarde. O corte é o do português falado - manhã até meio-dia, tarde até as
 * dezoito.
 *
 * A hora entra por parâmetro para o teste não depender do relógio da máquina.
 */
export function saudacao(hora: number = new Date().getHours()): string {
  if (hora < 12) return 'Bom dia';
  if (hora < 18) return 'Boa tarde';
  return 'Boa noite';
}
